/**
 * Forward navigation within the dynamic-survey region, honoring the paying
 * org's screener flags. Mirrors Sparta's per-screen "next step" epics
 * (setNextStepOfAssignedScreenersEpic → setNextStepOfStateComplianceDisclaimersEpic
 * → …): (dynamic-assessment, test arm only →) assigned-screeners →
 * state-compliance, then the org-gated pre-diabetes and phq-gad screens, then the
 * program region.
 *
 * Unlike the static survey (conditions → goals), the dynamic survey isn't a
 * fixed linear walk — pre-diabetes and phq-gad are included only when the org
 * requires them, so this can't be derived from the group order alone (see
 * `findNextStep`, which stays linear for the static survey). `goToNextStep`
 * calls this for the dynamic-survey steps and treats `null` as "survey done"
 * (→ markSurveyComplete, which lets the driver enter the program region).
 */

export type SurveyOrgFlags = {
  /** From `payingOrganization.preDiabetesRequired`. */
  preDiabetesRequired?: boolean;
  /** From `payingOrganization.phqGadRequired`. */
  phqGadRequired?: boolean;
  /**
   * True when the dynamic pass actually drove `pre_diabetes_v1` to `completed`
   * (its `/flows/` status, or a terminal the pass itself reached) — so the
   * standalone `/step/pre-diabetes-screen`
   * is skipped. Per-survey, NOT "were we in the test arm": if the flow was never
   * published or landed `abandoned` it never completes, so the standalone
   * screener still runs (fail-safe) rather than being silently dropped.
   *
   * Keyed on `pre_diabetes_v1` specifically. `diabetes_decision_v1` is NOT this
   * flow's counterpart — it's the diabetes TYPE / insulin-pump / medication-consent
   * screener, gated on the member already HAVING diabetes (webserver
   * `legacy_sync/diabetes.py`). Conflating the two silently drops the pre-diabetes
   * risk survey and its `prediabetes-risk` metric point.
   */
  preDiabetesServed?: boolean;
  /**
   * Same as `preDiabetesServed`, for `phq_gad_v1` → the standalone
   * `/step/phq-gad-questionnaire`. Guards the mental-health screener against a
   * silent drop when the flow is unpublished/abandoned.
   */
  phqGadServed?: boolean;
};

export const DYNAMIC_SURVEY_STEPS = [
  // Test-arm only: the dynamic-assessment pass (health conditions → goals → AOM →
  // diabetes → phq/gad as server-driven flows). The resolver enters the survey
  // region here in the test arm; on completion it advances to the legacy
  // assigned-screeners step below (the unmigrated-screener safety net).
  '/step/dynamic-assessment',
  '/step/assigned-screeners',
  '/step/state-compliance-disclaimer-page',
  '/step/pre-diabetes-screen',
  '/step/phq-gad-questionnaire',
] as const;

/** Whether `path` is one of the dynamic-survey steps (so org-aware nav applies). */
export function isDynamicSurveyStep(path: string | undefined): boolean {
  return path !== undefined && (DYNAMIC_SURVEY_STEPS as readonly string[]).includes(path);
}

// A step is gated only when its org flag is EXPLICITLY false. This intentionally
// diverges from Sparta's truthy check (`if (phqGadRequired)`), which skips on any
// falsy value including undefined: onboarding's payingOrganization flags are
// optional ("present when the linked org is fully loaded"), so defaulting an
// absent flag to "required" fails safe — it never silently drops the PHQ/GAD
// mental-health screener. Behavior matches Sparta whenever the API sends explicit
// booleans (the only divergence is the absent/undefined case).
function isStepIncluded(path: string, flags: SurveyOrgFlags): boolean {
  // Completed as a dynamic flow → the standalone screen is skipped (its survey
  // ran inside the /step/dynamic-assessment pass). Keyed per-survey: a flow that
  // wasn't served (unpublished / abandoned) is NOT marked served, so its
  // standalone screener still runs even in the test arm.
  if (path === '/step/pre-diabetes-screen') {
    return !flags.preDiabetesServed && flags.preDiabetesRequired !== false;
  }
  if (path === '/step/phq-gad-questionnaire') {
    return !flags.phqGadServed && flags.phqGadRequired !== false;
  }
  // dynamic-assessment, assigned-screeners, and state-compliance always run when
  // reached: dynamic-assessment is the test-arm entry (control never lands on it
  // — the resolver only routes there in the test arm), the legacy
  // assigned-screeners step runs in both arms, and state-compliance is
  // unconditional. Only the two standalone screens above are arm-dependent.
  return true;
}

/**
 * The next dynamic-survey step after `fromPath`, skipping screens the org
 * doesn't require, or `null` when none remain (the dynamic survey is done).
 * Returns `null` for a `fromPath` that isn't a dynamic-survey step.
 */
export function dynamicSurveyNextStep(
  fromPath: string | undefined,
  flags: SurveyOrgFlags
): string | null {
  if (!isDynamicSurveyStep(fromPath)) return null;
  const fromIndex = DYNAMIC_SURVEY_STEPS.indexOf(fromPath as (typeof DYNAMIC_SURVEY_STEPS)[number]);
  for (let i = fromIndex + 1; i < DYNAMIC_SURVEY_STEPS.length; i += 1) {
    const candidate = DYNAMIC_SURVEY_STEPS[i];
    if (isStepIncluded(candidate, flags)) return candidate;
  }
  return null;
}
